import { MeshBuilder, type Mesh } from "@babylonjs/core";
import { HeroId, Slot, ck, fxk, hv } from "@rivalwatch/shared";
import type { Cue } from "../../audio/engine.ts";
import { brass, click, clinks, drive, fm, later, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { AvatarClip, ClientHero, ConstructFrame } from "../types.ts";

const CK_TURRET = ck(HeroId.Punnisher, 0);
const FX_RIFLE = fxk(HeroId.Punnisher, 0);
const FX_TURRET = fxk(HeroId.Punnisher, 2);

type Gear = { rifle: Mesh; shotgun: Mesh; back: Mesh; eyes: Mesh; turretLamp: Mesh };

const rifleCue: Cue = trim(7, (o) => {
  click(o, 0.5, 3800, 0.004);
  thump(o, 0.25, 165, 70, 0.08);
  noise(drive(o, 1.8), { color: "pink", type: "bandpass", f: 2100, f1: 900, gain: 0.32, d: 0.08 });
  return 0.22;
});
const shotgunCue: Cue = trim(-1.5, (o) => {
  click(o, 0.65, 1900, 0.008);
  thump(o, 0.75, 95, 42, 0.18);
  noise(drive(o, 2.3), { color: "pink", type: "lowpass", f: 3200, f1: 520, gain: 0.55, d: 0.2 });
  clinks(o, 5, 0.045, 0.16, 900, 2800);
  return 0.55;
});
const smokeCue: Cue = trim(7, (o) => {
  thump(o, 0.35, 180, 75, 0.12);
  noise(o, { color: "brown", type: "lowpass", f: 700, f1: 180, gain: 0.5, a: 0.04, d: 0.55 });
  whoosh(o, 0.35, 900, 260, 0.45);
  return 0.9;
});
const cableCue: Cue = trim(10, (o) => {
  clinks(o, 11, 0.07, 0.28, 1500, 4300);
  whoosh(o, 0.35, 700, 2400, 0.24);
  fm(o, { f: 340, ratio: 2.1, index: 3, gain: 0.08, d: 0.18 });
  return 0.55;
});
const turretCue: Cue = trim(6, (o) => {
  thump(o, 0.45, 105, 50, 0.18);
  clinks(o, 8, 0.08, 0.35, 700, 2600);
  tone(later(o, 0.1), { type: "sawtooth", f: 95, f1: 180, glide: 0.35, gain: 0.05, d: 0.45, lp: 900 });
  return 0.8;
});
const ultCue: Cue = trim(5, (o) => {
  tone(drive(o, 2.8), { type: "sawtooth", f: 70, f1: 180, glide: 0.45, gain: 0.12, d: 0.55, lp: 950 });
  clinks(o, 12, 0.06, 0.42, 850, 3200);
  noise(o, { color: "crackle", type: "highpass", f: 2200, gain: 0.28, d: 0.45 });
  return 0.9;
});
const reloadCue = (seconds: number): Cue => trim(7.5, (o) => {
  clinks(o, 4, 0.08, seconds * 0.55, 900, 2600);
  click(o, 0.22, 1100, 0.012, seconds * 0.55);
  click(o, 0.25, 2400, 0.008, seconds * 0.82);
  return seconds + 0.15;
});

const hero: ClientHero<Gear> = {
  id: HeroId.Punnisher,
  bulk: 1.08,
  shape: { torso: 1.12, arms: 1.08 },
  gear(g) {
    const { scene, rig, add, dark, toon, glow } = g;
    const rifle = add(MeshBuilder.CreateBox("sentence-rifle", { width: 0.14, height: 0.18, depth: 1.05 }, scene), rig.handR, dark);
    rifle.position.z = -0.45;
    const muzzle = add(MeshBuilder.CreateCylinder("rifle-muzzle", { diameter: 0.13, height: 0.16, tessellation: 10 }, scene), rig.handR, glow("#ffd16a"));
    muzzle.rotation.x = Math.PI / 2;
    muzzle.position.z = -1;
    const shotgun = add(MeshBuilder.CreateBox("rebuttal-shotgun", { width: 0.26, height: 0.2, depth: 0.75 }, scene), rig.handL, toon("#3a3f47"));
    shotgun.position.z = -0.35;
    const back = add(MeshBuilder.CreateBox("ammo-backpack", { width: 0.55, height: 0.85, depth: 0.22 }, scene), rig.torso, dark);
    back.position.set(0, 0.15, 0.34);
    const visor = add(MeshBuilder.CreateBox("noir-visor", { width: 0.38, height: 0.06, depth: 0.05 }, scene), rig.head, glow("#ff4747", 0.8));
    visor.position.set(0, 0.04, -0.22);
    const turretLamp = add(MeshBuilder.CreateSphere("turret-lamp", { diameter: 0.14, segments: 8 }, scene), rig.torso, glow("#ffd16a"));
    turretLamp.position.set(0, 0.48, -0.34);
    return { rifle, shotgun, back, eyes: visor, turretLamp };
  },
  animate(gear, f): AvatarClip {
    const shotgun = f.v[0] > 0.5;
    gear.rifle.isVisible = !shotgun;
    gear.shotgun.isVisible = shotgun;
    const ult = f.ultActive ? 1 + Math.sin(f.t * 30) * 0.15 : 1;
    gear.back.scaling.set(1, ult, 1);
    gear.turretLamp.isVisible = f.ultActive || f.v[1] > 0;
    gear.turretLamp.scaling.setAll(f.v[3] > 0 ? 1.8 : 1);
    // The shooting pose only while a shot is going off (ActionKind Fire / AltFire), else the gun is just held.
    const firing = f.action === 1 || f.action === 2;
    if (f.v[3] > 0 || shotgun) return firing ? "holding-both-shoot" : "holding-both";
    return firing ? "holding-right-shoot" : "holding-right";
  },
  hud(c, eid) {
    return [
      { label: hv(c, eid, 0) > 0.5 ? "Shotgun" : "Rifle", value: 1 },
      { label: "Ult heat", value: hv(c, eid, 2) / 100, color: "#ffcc66" },
      { label: "Mounted", value: hv(c, eid, 3), color: "#ffd16a" },
    ];
  },
  constructs: {
    [CK_TURRET](scene, frame: ConstructFrame) {
      // Sized to the sim's 1.6 × 1.3 × 1.1 m box, with the barrel where the sim fires from (0.9 m).
      const root = MeshBuilder.CreateBox("mounted-argument", { width: 1.6, height: 0.7, depth: 1.3 }, scene);
      const barrel = MeshBuilder.CreateCylinder("argument-barrel", { diameter: 0.16, height: 1.2, tessellation: 10 }, scene);
      barrel.parent = root;
      barrel.rotation.x = Math.PI / 2;
      barrel.position.set(0, 0.55, -0.75);
      const seat = MeshBuilder.CreateBox("argument-seat", { width: 0.6, height: 0.35, depth: 0.5 }, scene);
      seat.parent = root;
      seat.position.set(0, 0.5, 0.4);
      const shield = MeshBuilder.CreateBox("argument-shield", { width: 1.3, height: 0.75, depth: 0.08 }, scene);
      shield.parent = root;
      shield.position.set(0, 0.55, -0.35);
      const update = (_: unknown, f: ConstructFrame) => {
        root.position.set(f.pos.x, f.pos.y + 0.35, f.pos.z);
        root.rotation.y = f.yaw;
        root.scaling.y = 0.75 + f.hpFrac * 0.25;
      };
      update(null, frame);
      return { gear: { root, barrel, seat, shield }, update, dispose: () => { root.dispose(); barrel.dispose(); seat.dispose(); shield.dispose(); } };
    },
  },
  fx: {
    [FX_RIFLE]: { keepGeneric: true },
    [FX_TURRET]: { keepGeneric: true },
  },
  audio: {
    weight: "mid",
    fire: (ctx) => ({ key: ctx?.ult ? "fire:punnisher:ult" : ctx?.alt ? "fire:punnisher:shotgun" : "fire:punnisher:rifle", cue: ctx?.ult ? ultCue : ctx?.alt ? shotgunCue : rifleCue }),
    reload: reloadCue,
    ability: {
      [Slot.Secondary]: smokeCue,
      [Slot.Ability1]: cableCue,
      [Slot.Ability2]: turretCue,
      [Slot.Ultimate]: ultCue,
    },
    theme: { bpm: 92, octave: -1, voice: brass, notes: [[0, 0.5], [2, 0.5], [5, 0.5], [0, 1]] },
    themeTrim: { heroic: -0.5, hostile: -6.5, tease: -2 },
  },
  icons: {
    [Slot.Primary]: "M4 12h16M7 8l10 8M8 16h8",
    [Slot.Secondary]: "M5 15c4-5 10-5 14 0M7 17h10",
    [Slot.Ability1]: "M4 12h8l8-5M12 12l8 5",
    [Slot.Ability2]: "M5 16h14M8 16V9h8v7M12 9V5",
    [Slot.Ultimate]: "M12 4v16M5 9h14M5 15h14",
    passive: "M12 5l4 7-4 7-4-7z",
  },
};

export default hero;
